import { ReactNode, useState } from "react";

interface ScreenProps {
  title: ReactNode;
  subtitle?: ReactNode;
  onBack?: () => void;
  /** 步驟進度（1~4），顯示頂部四點 */
  step?: number;
  children?: ReactNode;
  footer?: ReactNode;
  cardTitle?: ReactNode;
  /** 藍色 header 上的裝飾層（例：existVerify 的彩帶） */
  heroOverlay?: ReactNode;
  /** 白卡內容較長（如填地址）：往上捲時藍色 header 平滑收合、白卡順勢變長。 */
  collapsibleHeader?: boolean;
}

/** 入口/註冊頁共用版型：平面藍 header（含瓦斯桶裝飾）+ 底部圓角白卡。 */
const Screen = ({ title, subtitle, onBack, step, children, footer, cardTitle, heroOverlay, collapsibleHeader }: ScreenProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const headerShrunk = !!collapsibleHeader && collapsed;
  const onCardScroll = collapsibleHeader
    ? (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const y = el.scrollTop;
        const room = el.scrollHeight - el.clientHeight; // 目前還能捲多少
        // 收合會讓 header 變矮（約 100px）→ 白卡變高 → 可捲距離變短，若內容不夠長，
        // scrollTop 會被夾回頂端而觸發展開，展開又讓可捲距離變長…形成上下彈跳。
        // 故：內容需明顯長於 header 縮小量（room > 220）且捲夠深（y > 120）才收合；
        // 已收合則要回到接近頂端（y <= 8）才展開，維持大遲滯。
        setCollapsed((prev) => (prev ? y > 8 : room > 220 && y > 120));
      }
    : undefined;
  return (
    <div className="relative w-full h-[100dvh] flex flex-col overflow-hidden bg-[#2196f3]">
      {/* 中間層瓦斯桶裝飾（白卡背景 < 桶身 < 表單內容）：桶為白卡的兄弟層、非 header 子層，
          故不被 header/白卡 overflow 裁掉；z-[1] 疊在藍底與白卡背景上，但在 header 文字(z-[2])
          與表單內容(z-[2])之下。水位壓低藏在白卡後、不露出水面。 */}
      <svg viewBox="35 -165 210 1085" preserveAspectRatio="xMaxYMin meet" className="absolute z-[1] pointer-events-none" style={{ top: 60, right: -26, width: 116, height: 594 }} fill="none">
        <defs>
          <linearGradient id="onbGtBody" x1="0" y1="0" x2="0.3" y2="1">
            <stop offset="0" stopColor="#D8D6F7" stopOpacity="0.62" />
            <stop offset="0.5" stopColor="#B7BAF3" stopOpacity="0.52" />
            <stop offset="1" stopColor="#9AA6F0" stopOpacity="0.62" />
          </linearGradient>
          <linearGradient id="onbGtLiquid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#A6ABF4" stopOpacity="0.4" />
            <stop offset="1" stopColor="#8592F0" stopOpacity="0.75" />
          </linearGradient>
          <clipPath id="onbGtClipBody"><path d="M42 -75 Q42 -110 77 -110 L203 -110 Q238 -110 238 -75 L238 865 Q238 900 203 900 L77 900 Q42 900 42 865 Z" /></clipPath>
        </defs>
        <path d="M42 -75 Q42 -110 77 -110 L203 -110 Q238 -110 238 -75 L238 865 Q238 900 203 900 L77 900 Q42 900 42 865 Z" fill="url(#onbGtBody)" stroke="rgba(235,240,248,0.7)" strokeWidth="2" />
        <g clipPath="url(#onbGtClipBody)">
          <rect x="42" y="190" width="196" height="710" fill="url(#onbGtLiquid)" />
          <rect x="42" y="187" width="196" height="6" fill="#DADEFF" opacity="0.55" />
          <circle cx="100" cy="250" r="6" fill="#F0F2FF" opacity="0.45" />
          <circle cx="150" cy="310" r="4" fill="#F0F2FF" opacity="0.4" />
          <circle cx="190" cy="280" r="7" fill="#F0F2FF" opacity="0.35" />
        </g>
        <rect x="62" y="-50" width="9" height="890" rx="4" fill="#FFFFFF" opacity="0.5" />
        <path d="M92 -155 L188 -155 Q193 -155 193 -150 L193 -119 Q193 -114 188 -114 L92 -114 Q87 -114 87 -119 L87 -150 Q87 -155 92 -155 Z" fill="rgba(225,230,238,0.5)" stroke="rgba(235,240,248,0.7)" strokeWidth="2" />
        <rect x="96" y="-145" width="30" height="22" rx="2" fill="rgba(0,0,0,0.22)" stroke="rgba(235,240,248,0.7)" strokeWidth="1.2" />
        <rect x="154" y="-145" width="30" height="22" rx="2" fill="rgba(0,0,0,0.22)" stroke="rgba(235,240,248,0.7)" strokeWidth="1.2" />
        <rect x="124" y="-152" width="32" height="9" rx="2" fill="rgba(225,230,238,0.6)" stroke="rgba(235,240,248,0.7)" strokeWidth="1.2" />
        <rect x="135" y="-147" width="10" height="35" fill="rgba(225,230,238,0.7)" stroke="rgba(235,240,248,0.7)" strokeWidth="1.2" />
      </svg>
      <div
        className={`relative z-[2] shrink-0 overflow-hidden text-white px-[26px] ${onBack ? (headerShrunk ? "pt-[22px] pb-3" : "pt-[38px] pb-9") : (headerShrunk ? "pt-[28px] pb-3" : "pt-[50px] pb-[38px]")}`}
        style={{ transition: "padding 0.28s ease" }}>
        {heroOverlay && <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">{heroOverlay}</div>}
        {onBack && (
          <div onClick={onBack} className="relative inline-flex items-center gap-1 mb-3 cursor-pointer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span className="text-xs text-white/70">返回</span>
          </div>
        )}
        <div className="relative onb-rise">
          {/* 「瓦管家」小標移除後，補回原本那行佔的高度，維持標題位置不上移；收合時歸零。 */}
          <div style={{ height: headerShrunk ? 0 : 26, transition: "height 0.28s ease" }} />
          <div className="text-[27px] font-black tracking-[-0.5px] leading-[1.2]">{title}</div>
          {subtitle && (
            <div
              className="text-[13px] leading-[1.6]"
              style={{
                maxHeight: headerShrunk ? 0 : 240,
                opacity: headerShrunk ? 0 : 0.85,
                marginTop: headerShrunk ? 0 : 8,
                overflow: "hidden",
                transition: "max-height 0.28s ease, opacity 0.2s ease, margin-top 0.28s ease",
              }}>
              {subtitle}
            </div>
          )}
          {step && (
            <div className="flex gap-1.5 mt-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`h-[5px] rounded-[3px] flex-1 ${i <= step ? "bg-white" : "bg-white/30"}`} />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="relative z-[2] flex-1 min-h-0 flex flex-col overflow-hidden bg-white rounded-t-[28px]">
        <div className="onb-noscroll flex-1 min-h-0 overflow-auto px-[22px] py-6" onScroll={onCardScroll}>
          {cardTitle && <div className="text-xl font-extrabold text-[#222] mb-4">{cardTitle}</div>}
          {children}
        </div>
        {footer && <div className="px-[22px] pt-2 pb-6 flex flex-col gap-2.5">{footer}</div>}
      </div>
    </div>
  );
};

export default Screen;
